import React, { FC, useEffect, useState } from "react";
import { Box, Drawer, IconButton, Stack, Typography } from "@mui/material";
import { Settings as SettingsIcon, X, List } from "feather-icons-react";
import { useTranslation } from "react-i18next";

import type {
  AnalysisParameters,
  EducationTrainingItem,
  ProfessionalPositionItem,
  GradingItem,
} from "../../hooks";
import {
  ReferentialsSection,
  StandardParametersForm,
} from "../PaygapAnalysisParameters/components";

import { useGetCurrencies } from "@api";
import { useUserStore } from "@store/user";
import { Button } from "@components/base";

type TabType = "standard" | "referentials";

interface AnalysisParametersDrawerProps {
  open: boolean;
  onClose: () => void;
  parameters: AnalysisParameters;
  onParameterChange: (
    field: keyof AnalysisParameters,
    value: string | number,
  ) => void;
  onEducationAndTrainingChange: (
    index: number,
    field: keyof EducationTrainingItem,
    value: string | number,
  ) => void;
  onProfessionalPositionChange: (
    index: number,
    field: keyof ProfessionalPositionItem,
    value: string | number,
  ) => void;
  onAddEducationAndTraining: () => void;
  onRemoveEducationAndTraining: (index: number) => void;
  onAddProfessionalPosition: () => void;
  onRemoveProfessionalPosition: (index: number) => void;
  onGrading1Change: (index: number, field: keyof GradingItem, value: string) => void;
  onGrading2Change: (index: number, field: keyof GradingItem, value: string) => void;
  onGrading3Change: (index: number, field: keyof GradingItem, value: string) => void;
  onAddGrading1: () => void;
  onAddGrading2: () => void;
  onAddGrading3: () => void;
  onRemoveGrading1: (index: number) => void;
  onRemoveGrading2: (index: number) => void;
  onRemoveGrading3: (index: number) => void;
  onSaveReferentials: () => void;
  hasIdConflicts: boolean;
  showSaveSuccess: boolean;
}

export const AnalysisParametersDrawer: FC<AnalysisParametersDrawerProps> = ({
  open,
  onClose,
  parameters,
  onParameterChange,
  onEducationAndTrainingChange,
  onProfessionalPositionChange,
  onAddEducationAndTraining,
  onRemoveEducationAndTraining,
  onAddProfessionalPosition,
  onRemoveProfessionalPosition,
  onGrading1Change,
  onGrading2Change,
  onGrading3Change,
  onAddGrading1,
  onAddGrading2,
  onAddGrading3,
  onRemoveGrading1,
  onRemoveGrading2,
  onRemoveGrading3,
  onSaveReferentials,
  hasIdConflicts,
  showSaveSuccess,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { currencies, currenciesEnabled } = useGetCurrencies(user);
  const [activeTab, setActiveTab] = useState<TabType>("standard");

  // Reset to standard tab when drawer opens
  useEffect(() => {
    if (open) {
      setActiveTab("standard");
    }
  }, [open]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        width={{ xs: 520, sm: 700 }}
        p={3}
        display="flex"
        flexDirection="column"
        gap={2}
      >
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="h6">{t("paygap.parameters.title")}</Typography>
          <IconButton onClick={onClose} size="small">
            <X size={16} />
          </IconButton>
        </Box>

        {/* Tab buttons */}
        <Stack direction="row" spacing={1}>
          <Button
            variant={activeTab === "standard" ? "contained" : "outlined"}
            size="small"
            startIcon={<SettingsIcon size={14} />}
            onClick={(): void => setActiveTab("standard")}
          >
            {t("paygap.parameters.page.standard")}
          </Button>
          <Button
            variant={activeTab === "referentials" ? "contained" : "outlined"}
            size="small"
            startIcon={<List size={14} />}
            onClick={(): void => setActiveTab("referentials")}
          >
            {t("paygap.parameters.page.referentials")}
          </Button>
        </Stack>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {activeTab === "standard" && (
            <StandardParametersForm
              parameters={parameters}
              onParameterChange={onParameterChange}
              currencies={currencies}
              currenciesEnabled={currenciesEnabled}
              onSave={onSaveReferentials}
              showSaveSuccess={showSaveSuccess}
            />
          )}

          {activeTab === "referentials" && (
            <ReferentialsSection
              parameters={parameters}
              onEducationAndTrainingChange={onEducationAndTrainingChange}
              onProfessionalPositionChange={onProfessionalPositionChange}
              onAddEducationAndTraining={onAddEducationAndTraining}
              onRemoveEducationAndTraining={onRemoveEducationAndTraining}
              onAddProfessionalPosition={onAddProfessionalPosition}
              onRemoveProfessionalPosition={onRemoveProfessionalPosition}
              onGrading1Change={onGrading1Change}
              onGrading2Change={onGrading2Change}
              onGrading3Change={onGrading3Change}
              onAddGrading1={onAddGrading1}
              onAddGrading2={onAddGrading2}
              onAddGrading3={onAddGrading3}
              onRemoveGrading1={onRemoveGrading1}
              onRemoveGrading2={onRemoveGrading2}
              onRemoveGrading3={onRemoveGrading3}
              onSaveReferentials={onSaveReferentials}
              hasIdConflicts={hasIdConflicts}
              showSaveSuccess={showSaveSuccess}
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default AnalysisParametersDrawer;
